export const enum PresentationEventTypes {
    /**
     * 请求暂存一组变更，待事务结束时，进行应用
     */
    EnqueueMutations = 'enqueue-mutations',
    /**
     * 提交 Mutations 到 CommandManager
     */
    CommitCommand = 'commit-command',
    /**
     * 请求提交一组变更到服务端
     */
    SubmitMutations = 'submit-mutations',
    /**
     * mutation 已经应用，在发送/进栈前
     */
    MutationsApplied = 'mutations-applied',
    /**
     * 标记 Mutation 来源
     */
    MutationSource = 'mutation-source',
    /**
     * 变更应用期间发生错误
     */
    MutationError = 'mutation-error',
    /**
     * 请求开启一段事务，事务开始期间有新的事务，则形成嵌套事务
     */
    RequestBeginTransaction = 'request-begin-transaction',
    /**
     * 请求结束一段事务，结束时会 Apply 并清空 MutationQueue，嵌套事务最外层结束时，会提交事务
     */
    RequestEndTransaction = 'request-end-transaction',
    /**
     * 一段事务开始前，会发起此事件
     */
    BeginTransaction = 'begin-transaction',
    /**
     * 一段事务结束时，会发起此事件
     */
    EndTransaction = 'end-transaction',
    /**
     * 最外层事务结束时，会发起此事件，此时，会集中做变更通知
     */
    CommitTransaction = 'commit-transaction',
    /**
     * 用户进行编辑行为后，会发起此事件
     */
    Edit = 'edit',
    /**
     * 请求绑定新的 DataModel 到当前的文档模型中
     */
    AttachDataModel = 'attach-data-model',
    /**
     * 请求解除 DataModel 与当前文档模型的绑定
     */
    DetachDataModel = 'detach-data-model',
    /**
     * 请求暂存变更通知，待收到 CommitChanges 事件时，统一通知到观察者
     */
    EnqueueChange = 'enqueue-change',
    /**
     * sdk 编辑操作开始通知
     * sdk 编辑操作目前包括：undo redo 和各个 api接口
     * 注意此事件监听不可以抛错，否则会影响业务逻辑，监听函数可以用 try catch 包裹
     */
    OperationBegin = 'operation-begin',
    /**
     * sdk 编辑操作结束通知
     * 注意此事件监听不可以抛错，否则会影响业务逻辑
     */
    OperationEnd = 'operation-end',
    /**
     * sdk 编辑操作出错通知
     */
    OperationError = 'operation-error',
    /**
     * 某个 model 需要监听 resourceMap
     */
    ModelObserveResourceMap = 'model-observe-resource-map',
    /**
     * resourceMap 变更通知
     */
    ResourceMapChange = 'resource-map-change',
    /**
     * 分页加载中页面状态变更
     */
    PageLoadingStateChange = 'page-loading-state-change',
}

export const enum RevisionEventTypes {
    // 提交成功事件
    CommitSucceed = 'commit-succeed',

    // 提交失败事件
    CommitFail = 'commit-fail',

    // 更新版本号
    UpdateBaseRevision = 'update-revision-base-revision',

    // 在线转离线
    ChangeToOffline = 'change-to-offline',

    // 离线转在线
    ChangeToOnline = 'change-to-online',

    // 进入待发送队列
    Enqueue = 'enqueue',

    // 拉取断层失败
    FetchMissingDataError = 'fetch-missing-data-error',

    // 提交的数据 baseVersion 过低
    BaseVersionToLow = 'base-version-to-low',

    // 权限变化，无权限提交
    NoPrivilegeToSubmit = 'no-privilege-to-submit',
}

export const enum CommandEventTypes {
    /**
     * Undo/Redo 栈发生变化时，会发起此事件
     */
    UndoRedoChange = 'undo-redo-change',

    Undo = 'undo',

    Redo = 'redo',

    ClearCommand = 'clear-command',

    Error = 'command-error',
}

export const enum SocketEventTypes {
    /**
     * 服务端发来消息
     */
    ReceiveMessage = 'receive-message',
    /**
     * 服务端发来新的变更
     */
    NewChanges = 'new-changes',
    /**
     * 服务端关闭
     */
    ShutDown = 'shut-down',
    /**
     * 用户进入
     */
    UserEnter = 'user-enter',
    /**
     * 用户退出
     */
    UserLeave = 'user-leave',
    /**
     * 光标信息
     */
    CursorMessage = 'cursor-message',
    /**
     * 权限信息
     */
    RightMessage = 'right-message',
    /**
     * 标题信息
     */
    TitleMessage = 'title-message',
    /**
     * 文档删除
     */
    DeleteDoc = 'delete-doc',
    /**
     * 最新版本号
     */
    LatestVersion = 'latest-version',
    /**
     * 修订记录还原
     */
    RevertMessage = 'revert-message',
    /**
     * 协同加锁
     */
    AllLockInfo = 'all-lock-info',
    /**
     * 协同解锁
     */
    UnLockInfo = 'un-lock-info',
    /**
     * 水印变更通知
     */
    WaterMarkChange = 'water-mark-change',
    /**
     * 离转在
     */
    ChangeToOnline = 'change-to-online',
    /**
     * 在转离
     */
    ChangeToOffline = 'change-to-offline',
    /**
     * 演示态跟随
     */
    PresentInfo = 'present-info',

    // 隐身查看
    AnonymousChange = 'anonymous-change',

    /**
     * webOffice revert
     */
    WebOfficeRevert = 'web-office-revert',

    /**
     * 私有化上传新版本触发，与revert数据结构一致
     */
    CommitUpload = 'commit-upload',
}

export const enum PersistentEventTypes {
    RecoveryStatusChange = 'recovery-status-change',
    // 获取所有持久化的数据
    GetAllMutationsPacks = 'persistent-changeset-getall',
    // 新增/修改持久化数据
    AddOrUpdateData = 'add-or-update-data',
    // 删除持久化数据
    RemoveData = 'remove-data',
    // 替换所有持久化数据
    ReplaceAllData = 'replace-all-data',
    // 数据库打开失败
    OpenDataBaseError = 'open-data-base-error',
    // 数据库严重损坏
    DataBaseFatalError = 'data-base-fatal-error',
    // DB 初始化成功
    DataBaseInitialized = 'data-base-initialized',
}

export const enum OTTransformEventTypes {
    TransformError = 'transform-error',
    TransformTimeout = 'transform-timeout',
}

export const enum CollaborationEventTypes {
    FatalError = 'fatal-error',
    StatusChange = 'collaboration-status-change',
}
